import { T } from '@transifex/react';
import { VStack } from '@vidahealth/vida-ui';
import { useCallback, useEffect } from 'react';

import { useOnboardingSteps } from '@/modules/rulesEngine';
import { CtaButton, LoadingScreen, Markdown, Screen, ScreenHeading } from '@/modules/ui';

import { useStateComplianceDisclaimers } from './hooks/useStateComplianceDisclaimers';

/**
 * State-compliance disclaimers — shown (between pre-diabetes and phq-gad) only
 * for users whose state requires them. No submission; acknowledging advances.
 * An empty list or load error skips ahead (most users have no disclaimers).
 *
 * `untranslatedDisclaimerDescription` is markdown on the wire — rendered via <Markdown> so
 * embedded links (attorneys-general info, opt-out forms) and lists work.
 */
export function StateComplianceDisclaimerScreen() {
  const { goToNextStep } = useOnboardingSteps();
  const { disclaimers, isLoading, error } = useStateComplianceDisclaimers();

  const advance = useCallback(
    () => goToNextStep({ from: '/step/state-compliance-disclaimer-page' }),
    [goToNextStep]
  );

  const shouldAdvance = !isLoading && (error != null || disclaimers.length === 0);
  useEffect(() => {
    if (shouldAdvance) advance();
  }, [shouldAdvance, advance]);

  if (isLoading || shouldAdvance) {
    return <LoadingScreen />;
  }

  return (
    <Screen
      footer={
        <CtaButton onPress={advance} testID='state-compliance-continue'>
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        {disclaimers.map((disclaimer) => (
          <VStack key={disclaimer.uuid} space='md'>
            <ScreenHeading title={disclaimer.untranslatedDisclaimerTitle} />
            <Markdown>{disclaimer.untranslatedDisclaimerDescription}</Markdown>
          </VStack>
        ))}
      </VStack>
    </Screen>
  );
}
